import { isUsableImageUrl } from "./imageUrl"
import { isoDuration } from "./sceneDuration"

// Pure builders for the schema.org JSON-LD blocks. The composables
// (useSiteSchema, useSceneSchema) feed them request and page data.

// CloudFront talks to the origin over plain HTTP, so the request URL says
// http:// in production. Every public host is HTTPS-only; only local dev hosts
// keep the scheme they were served on. Mirrors useSiteSeo's canonical URL.
export const publicSiteOrigin = (requestUrl) => {
  const origin = new URL(requestUrl)
  const local = /(^localhost$|^127\.|\.lvh\.me$|\.lan$)/.test(origin.hostname)
  if (!local) origin.protocol = "https:"
  return origin.origin
}

// Serialized for a <script> element: escaping every "<" keeps user-entered
// strings from closing the element ("</script>") or opening a comment ("<!--").
export const serializeJsonLd = (data) =>
  JSON.stringify(data).replace(/</g, "\\u003c")

export const organizationId = (origin) => `${origin}/#organization`

// The Person @id is shared with the performer page, so engines can join the
// scene's cast to the model profile.
export const personId = (origin, slug) => `${origin}/models/${slug}#person`

// `site` is the current site; on an alias host its `alias` carries that
// host's brand. Alias SEO fields are optional, so each one falls back to the
// site's own.
export const buildSiteGraphSchema = ({ origin, site: base, logoPath }) => {
  const alias = base?.alias
  const site = {
    name: base?.name,
    seoSiteName: alias?.seoSiteName || base?.seoSiteName,
    seoSiteNameWithTld: alias?.seoSiteNameWithTld || base?.seoSiteNameWithTld,
  }
  const name = site.seoSiteName || site.name
  if (!origin || !name) return null

  const organization = {
    "@type": "Organization",
    "@id": organizationId(origin),
    name,
    url: `${origin}/`,
  }
  if (logoPath) organization.logo = `${origin}${logoPath}`

  const website = {
    "@type": "WebSite",
    "@id": `${origin}/#website`,
    name,
    url: `${origin}/`,
    inLanguage: "en",
    publisher: { "@id": organizationId(origin) },
  }
  // The nav and breadcrumb spell some brands differently ("Tiny4K" vs the SEO
  // name "Tiny 4K"), so list those spellings too.
  const altNames = [
    ...new Set([site.name, site.seoSiteNameWithTld].filter(Boolean)),
  ].filter((alt) => alt !== name)
  if (altNames.length) {
    website.alternateName = altNames.length === 1 ? altNames[0] : altNames
  }

  return { "@context": "https://schema.org", "@graph": [organization, website] }
}

// Matches the visible breadcrumb: Site › Title.
export const buildSceneBreadcrumbSchema = ({ origin, siteName, title }) => {
  if (!origin || !siteName || !title) return null

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: siteName, item: `${origin}/` },
      { "@type": "ListItem", position: 2, name: title },
    ],
  }
}

const isoDate = (value) => {
  if (!value) return null

  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

// Returns null when a field Google requires (name, thumbnailUrl, uploadDate,
// description) is missing, rather than emitting an invalid item.
//
// `duration` only when the API has one. Left out on purpose: contentUrl/embedUrl
// (trailer URLs are signed and expire; there is no embed route),
// interactionStatistic (no view count) and isAccessibleForFree (Google pairs
// it with paywalled-section markup, and the paid video isn't on the page).
export const buildSceneVideoSchema = ({
  origin,
  path,
  siteName,
  release,
  description,
  images = [],
  tagLabels = null,
}) => {
  const name = release?.title
  const uploadDate = isoDate(release?.releasedAt)
  const thumbnailUrl = [
    ...new Set(images.filter((url) => isUsableImageUrl(url))),
  ]
  const text = description?.trim()
  if (!origin || !name || !uploadDate || !thumbnailUrl.length || !text) {
    return null
  }

  const url = `${origin}${path}`
  // Readable labels, as the page's tag list shows them: the tag's own label
  // when the page passes them (teen → teen 18+), else big_dick → big dick.
  const tags =
    tagLabels ?? (release.tags ?? []).map((tag) => tag.replace(/[_-]+/g, " "))
  const actors = (release.actors ?? []).filter((actor) => actor?.name)

  const video = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "@id": `${url}#video`,
    url,
    name,
    description: text,
    thumbnailUrl,
    uploadDate,
    datePublished: uploadDate,
    inLanguage: "en",
    isFamilyFriendly: false,
    publisher: { "@id": organizationId(origin) },
    productionCompany: { "@id": organizationId(origin) },
  }

  const duration = isoDuration(release.durationSeconds)
  if (duration) video.duration = duration

  if (actors.length) {
    video.actor = actors.map((actor) =>
      actor.cached_slug
        ? {
            "@type": "Person",
            "@id": personId(origin, actor.cached_slug),
            name: actor.name,
            url: `${origin}/models/${actor.cached_slug}`,
          }
        : { "@type": "Person", name: actor.name },
    )
  }

  if (tags.length) video.genre = tags

  const keywords = [...tags, ...actors.map((actor) => actor.name), siteName]
  video.keywords = keywords.filter(Boolean).join(", ")

  // Same rounding as the page's "Rated 9.2 / 10 (796 votes)", which Google
  // requires to be visible.
  const ratingValue = Number((release.ratingsDecimal ?? 0).toFixed(1))
  const ratingCount = release.voteCount ?? 0
  if (ratingValue > 0 && ratingCount > 0) {
    video.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue,
      bestRating: 10,
      worstRating: 1,
      ratingCount,
    }
  }

  return video
}

// A trail of `{ name, path }` crumbs, the last one being the current page
// (no `path`, so no link), as the visible breadcrumb shows it.
export const buildBreadcrumbSchema = ({ origin, trail }) => {
  if (!origin || !trail?.length) return null

  return {
    "@type": "BreadcrumbList",
    itemListElement: trail.map((crumb, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: crumb.name,
      ...(crumb.path ? { item: `${origin}${crumb.path}` } : {}),
    })),
  }
}

// CollectionPage for a hub page (/tags, /tags/{slug}, /tags/{a}/{b}): the page,
// its breadcrumb, and an ItemList of the pages it lists, in order. `url` is the
// page's canonical URL; `startPosition` numbers the items on later pages of a
// paginated list from where the previous page left off. `itemListOrder`
// ("Ascending" or "Descending") says how the list is sorted, when it is.
export const buildCollectionPageSchema = ({
  origin,
  url,
  name,
  description,
  trail,
  items,
  startPosition = 1,
  itemListOrder = null,
}) => {
  if (!origin || !url || !name) return null

  const page = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${url}#webpage`,
    url,
    name,
    isPartOf: { "@id": `${origin}/#website` },
  }
  if (description) page.description = description

  const breadcrumb = buildBreadcrumbSchema({ origin, trail })
  if (breadcrumb) page.breadcrumb = breadcrumb

  if (items?.length) {
    page.mainEntity = {
      "@type": "ItemList",
      ...(itemListOrder && {
        itemListOrder: `https://schema.org/ItemListOrder${itemListOrder}`,
      }),
      numberOfItems: items.length,
      itemListElement: items.map((item, i) => ({
        "@type": "ListItem",
        position: startPosition + i,
        url: `${origin}${item.path}`,
        name: item.name,
      })),
    }
  }

  return page
}

// ProfilePage for a performer page (/models/{slug}). Its Person carries the
// same @id as the `actor` entries on the performer's scene pages, so engines
// join the profile to the scenes; the scenes themselves aren't repeated here.
export const buildProfilePageSchema = ({
  origin,
  url,
  slug,
  name,
  image,
  description,
  trail,
}) => {
  if (!origin || !url || !slug || !name) return null

  const person = {
    "@type": "Person",
    "@id": personId(origin, slug),
    name,
    url: `${origin}/models/${slug}`,
  }
  if (isUsableImageUrl(image)) person.image = image

  const page = {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    "@id": `${url}#webpage`,
    url,
    name,
    isPartOf: { "@id": `${origin}/#website` },
    mainEntity: person,
  }
  if (description) page.description = description

  const breadcrumb = buildBreadcrumbSchema({ origin, trail })
  if (breadcrumb) page.breadcrumb = breadcrumb

  return page
}
